import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Space, Spin, Alert } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelFarmaciasCU2SinVincular,
  type FarmaciaCU2SinVincular,
} from '../../api/vinculacionApi';
import { exportCsv, FilterBtn, cmpNum, cmpTxt } from './helpers';

const VinculacionFarmaciasCU2SinVincularTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<FarmaciaCU2SinVincular[]>([]);
  const [filtro, setFiltro] = useState<'' | 'con' | 'sin'>('');

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setRows(await getPanelFarmaciasCU2SinVincular());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(
    () => ({
      con: rows.filter((r) => r.tieneTitular).length,
      sin: rows.filter((r) => !r.tieneTitular).length,
    }),
    [rows],
  );

  const data =
    filtro === 'con'
      ? rows.filter((r) => r.tieneTitular)
      : filtro === 'sin'
        ? rows.filter((r) => !r.tieneTitular)
        : rows;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las farmacias CU2 sin vincular"
        action={<Button onClick={load}>Reintentar</Button>}
      />
    );
  }

  const columns: ColumnsType<FarmaciaCU2SinVincular> = [
    {
      title: 'ID CU2',
      dataIndex: 'idFarmacia',
      align: 'right',
      width: 90,
      sorter: (a, b) => cmpNum(a.idFarmacia, b.idFarmacia),
    },
    {
      title: 'Farmacia',
      dataIndex: 'nombreFarmacia',
      sorter: (a, b) => cmpTxt(a.nombreFarmacia, b.nombreFarmacia),
      ellipsis: true,
    },
    {
      title: 'Dirección',
      dataIndex: 'direccion',
      sorter: (a, b) => cmpTxt(a.direccion, b.direccion),
      ellipsis: true,
    },
    {
      title: 'CP',
      dataIndex: 'cp',
      width: 70,
      sorter: (a, b) => cmpTxt(a.cp, b.cp),
    },
    {
      title: 'Localidad',
      dataIndex: 'localidad',
      sorter: (a, b) => cmpTxt(a.localidad, b.localidad),
      ellipsis: true,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      sorter: (a, b) => cmpTxt(a.provincia, b.provincia),
    },
    {
      title: 'Titular',
      dataIndex: 'tieneTitular',
      width: 100,
      align: 'center',
      sorter: (a, b) => Number(a.tieneTitular) - Number(b.tieneTitular),
      render: (v: boolean) =>
        v ? <Tag color="green">Sí</Tag> : <Tag color="orange">No</Tag>,
    },
  ];

  const handleExport = () => {
    exportCsv(
      'farmacias_cu2_sin_vincular.csv',
      ['ID CU2', 'Farmacia', 'Dirección', 'CP', 'Localidad', 'Provincia', 'Titular'],
      data.map((r) => [
        r.idFarmacia,
        r.nombreFarmacia ?? '',
        r.direccion ?? '',
        r.cp ?? '',
        r.localidad ?? '',
        r.provincia ?? '',
        r.tieneTitular ? 'Sí' : 'No',
      ]),
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <FilterBtn active={filtro === ''} onClick={() => setFiltro('')}>
            Todas ({rows.length})
          </FilterBtn>
          <FilterBtn active={filtro === 'con'} onClick={() => setFiltro('con')}>
            Con titular ({counts.con})
          </FilterBtn>
          <FilterBtn active={filtro === 'sin'} onClick={() => setFiltro('sin')}>
            Sin titular ({counts.sin})
          </FilterBtn>
        </Space>
        <Space>
          <Button icon={<DownloadOutlined />} size="small" onClick={handleExport}>
            CSV
          </Button>
          <Button icon={<ReloadOutlined />} size="small" onClick={load}>
            Recargar
          </Button>
        </Space>
      </div>

      <Table<FarmaciaCU2SinVincular>
        dataSource={data}
        columns={columns}
        rowKey="idFarmacia"
        size="small"
        pagination={{ pageSize: 100, showSizeChanger: true, showTotal: (t) => `${t} registros` }}
      />
    </div>
  );
};

export default VinculacionFarmaciasCU2SinVincularTab;
